import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  IconButton,
} from "@mui/material";
import {
  CalendarMonth as BookingIcon,
  EventNote as JournalIcon,
  People as ClientsIcon,
  AttachMoney as FinanceIcon,
  BarChart as StatsIcon,
  ContentCut as ServicesIcon,
  PhotoLibrary as PhotosIcon,
  AttachMoney as SalaryIcon,
  People as UsersIcon,
  LocationOn as LocationsIcon,
} from "@mui/icons-material";
import Header from "../components/Header";

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
  disabled?: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ title, description, icon, color, onClick, disabled = false }) => {
  return (
    <Card
      onClick={disabled ? undefined : onClick}
      sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        "&:hover": disabled ? {} : {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
          borderColor: color,
        },
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <CardContent sx={{ p: 3, textAlign: "center" }}>
        <Avatar
          sx={{
            bgcolor: color,
            width: 60,
            height: 60,
            mx: "auto",
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "text.primary" }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem" }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("user_role") || "client";

  const modules = [
    {
      title: "Запись",
      description: "Записать клиента на услугу",
      icon: <BookingIcon />,
      color: "#2196F3",
      route: "/booking",
    },
    {
      title: "Мои фото",
      description: "Референсы и фото после",
      icon: <PhotosIcon />,
      color: "#607D8B",
      route: "/my-photos",
      clientOnly: true,
    },
    {
      title: "Журнал записей",
      description: "Список, перенос и отмена записей",
      icon: <JournalIcon />,
      color: "#3F51B5",
      route: "/bookings",
      restricted: true,
    },
    {
      title: "Клиенты",
      description: "База данных клиентов",
      icon: <ClientsIcon />,
      color: "#4CAF50",
      route: "/clients",
      adminOnly: true,
    },
    {
      title: "Финансы",
      description: "Доходы и расходы",
      icon: <FinanceIcon />,
      color: "#FF9800",
      route: "/finance",
      adminOnly: true,
    },
    {
      title: "Статистика",
      description: "Аналитика и отчеты",
      icon: <StatsIcon />,
      color: "#9C27B0",
      route: "/dashboard",
      adminOnly: true,
    },
    {
      title: "Услуги",
      description: "Прайс-лист услуг",
      icon: <ServicesIcon />,
      color: "#F44336",
      route: "/services",
      clientHidden: true,
    },
    {
      title: "Пользователи",
      description: "Управление сотрудниками",
      icon: <UsersIcon />,
      color: "#FF5722",
      route: "/users",
      adminOnly: true,
    },
    {
      title: "Места",
      description: "Филиалы и адреса",
      icon: <LocationsIcon />,
      color: "#9C27B0",
      route: "/locations",
      adminOnly: true,
    },
    {
      title: "Зарплата",
      description: "Расчет зарплаты мастеров",
      icon: <SalaryIcon />,
      color: "#795548",
      route: "/salary",
      restricted: true,
    },
  ].filter(module => {
    if (module.adminOnly && userRole !== "admin") return false;
    if (module.restricted && !["admin", "master"].includes(userRole)) return false;
    if ((module as any).clientHidden && userRole === "client") return false;
    if ((module as any).clientOnly && userRole !== "client") return false;
    return true;
  });

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Зеркало" />
      
      <Box sx={{ p: { xs: 2, sm: 4 } }}>
        {/* Модули */}
        <Grid container spacing={3} justifyContent="center">
          {modules.map((module, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <ModuleCard
                title={module.title}
                description={module.description}
                icon={module.icon}
                color={module.color}
                onClick={() => navigate(module.route)}
                disabled={module.adminOnly && userRole !== "admin"}
              />
            </Grid>
          ))}
        </Grid>

        {/* Подвал */}
        <Box sx={{ textAlign: "center", mt: 8, py: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Dagzhi family • Система управления студией красоты
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
